/**
 * product-create/types — 제품 등록 한 폼의 상태(VOCABULARY "제품 등록 — 한 폼", 2026-10-22 · 회의 2026-10-21 §2).
 * 폼은 글만 쥔다. 카테고리(21종 · CHASSIS 포함) · 번호 종류 · 파트넘버 · 디스크립션(셋 필수) · 검색어 · 메모.
 * 카탈로그는 `/catalog?pick=1` 새 탭에서 골라 `picked` 로 들어온다. 순수 함수는 `form.ts`, 상태는 뷰의 this.data.
 */
import type { SpecCategory } from '@hcltech/shared/server-config/spec-category';
import type { CatalogPartView, CatalogPick, DescriptionReading, DescriptionRuleView, EPartNumberColumn, OpenRequestRef, ProductRef, SlotFill } from '../../api/client';

/** 찾기 결과 — `catalog` 는 파트 찾기(`v_catalog_part` 17표 · 섀시 포함), `matched_column` 은 친 번호가 맞은 번호 칸(번호 한 칸 — 2026-10-29), `product` 는 같은 번호의 제품(섀시 폼은 서버 찾기의 것). 카탈로그에 있으면 카테고리를 잠근다 */
export type FormLookup = { catalog: CatalogPartView | null; matched_column: EPartNumberColumn | null; product: ProductRef | null; open_request: OpenRequestRef | null };   // open_request — 친 번호의 열린 요청(승격이 먼저, 2026-10-30)

/** 사람이 치는 칸 일곱 — `formWithField` 의 키. 값은 전부 글(셀렉트도). `catalog_query` 는 제품 칸이 아니라 카탈로그 탭에 넘길 찾기 글(2026-10-22 현민 "검색어를 적고 찾기") */
export const E_PRODUCT_FORM_FIELDS = ['catalog_query', 'spec_category', 'part_number', 'vendor', 'description', 'description_rule_id', 'search_text', 'notes'] as const;   // description_rule_id 셀렉트 값 '' = 기본
export type EProductFormField = typeof E_PRODUCT_FORM_FIELDS[number];

/** 못 찾은 글을 넣을 칸 — 파트넘버 · 디스크립션 둘(2026-10-26 A — 찾기 칸 글이 카탈로그에 없을 때) */
export const E_FIND_FILL_FIELDS = ['part_number', 'description'] as const;
export type EFindFillField = typeof E_FIND_FILL_FIELDS[number];

/** 등록 폼 한 장의 상태 */
export type ProductFormData = {
  missed_query: string | null;        // 못 찾은 글 — [찾기] 했는데 카탈로그에 한 건도 없던 찾기 칸 글. null = 안내 없음. catalog_query 를 고치면 null
  finding: boolean;                   // [찾기] 가 결과 수를 보는 중 — 버튼 잠금
  catalog_query: string;              // [찾기] 가 `/catalog?pick=1&q=` 로 넘기는 글 — 파트넘버 · 디스크립션. 저장엔 안 간다
  spec_category: SpecCategory | '';   // '' = 아직 안 고름. CHASSIS 면 서버 등록(POST /products/server · 구성 없음)
  part_number: string;                // 번호 한 칸 — 종류는 조사가 가른다(2026-10-29)
  vendor: string;                     // 제조사 — 선택. 카탈로그에 이어지면 카탈로그 것(칸 잠김)
  vendor_other: boolean;              // 제조사 셀렉트에서 "기타 — 직접 적기" 를 골랐나 — 막 고른 순간엔 vendor 가 비어 값만으론 못 가른다(2026-10-30)
  vendors: string[];                  // 제조사 후보(GET /products/vendors) — 페이지 열 때 한 번
  description: string;
  description_rule_id: string | null; // 고른 규칙. null = 카테고리 기본 — 카테고리를 바꾸면 null 로
  rules: DescriptionRuleView[];       // 그 카테고리 규칙(규칙 고르기 셀렉트의 선택지) — 카테고리를 바꾸면 다시 받는다
  reading: DescriptionReading | null; // 규칙 읽기 — null = 아직(빈 글 · 받는 중 · 읽기 안 보이는 때)
  slot_fills: SlotFill[];             // 채운 자리 — 친 그대로(빈 글은 보낼 때 뺀다 · slotFillsOf). 카테고리 · 규칙을 바꾸면 []
  search_text: string;
  notes: string;
  picked: CatalogPick | null;         // 카탈로그에서 담아 온 행 — 섀시 저장에 chassis_model_id 를 넘긴다. 칸을 손으로 고치면 null
  lookup: FormLookup | null;          // null = 아직(빈 칸 · 받는 중)
  saving: boolean;
  error: string;
};
